import React, { useEffect, useState } from 'react';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/use-toast';
import { InfoIcon, AlertCircleIcon, CheckCircleIcon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useImportFieldSchema } from './hooks/useImportFieldSchema';
import { useSkuValidation } from './hooks/useSkuValidation';
import { SkuValidationWarning } from './components/SkuValidationWarning';
import { Skeleton } from '@/components/ui/skeleton';
import { Mapping } from '@/types/import';
import {
  ImportFieldSchemaEntry,
  DuplicateStrategy,
  getFamilyAttributes,
} from '@/services/importService';
import { ImportMode } from './StepImportMode';
import { useImportTranslations } from '@/hooks/useImportTranslations';
import { captureValidationError } from '@/lib/errorReporting';
import { createLogger } from '@/lib/logger';
import { logError } from '@/utils/errorHandler';

type StructureType = 'attribute_groups' | 'attributes' | 'families' | null;

interface StepMappingProps {
  sourceHeaders: string[];
  onMappingComplete: (mapping: Mapping) => void;
  fieldSchema?: ImportFieldSchemaEntry[] | null;
  previewData?: any[];
  fullData?: any[];
  importMode?: ImportMode;
  structureType?: StructureType;
  duplicateStrategy?: DuplicateStrategy;
}

// Storage key for saved mapping templates
const MAPPING_STORAGE_KEY = 'kl_last_mapping';

// Global cache for family attributes to survive component remounts
const globalFamilyAttrCache: Record<string, string[]> = {};

// Create component-specific logger
const stepMappingLogger = createLogger('StepMapping');

const StepMapping: React.FC<StepMappingProps> = ({
  sourceHeaders,
  onMappingComplete,
  fieldSchema: propFieldSchema,
  previewData = [],
  fullData,
  importMode,
  structureType: _structureType,
  duplicateStrategy = 'overwrite',
}) => {
  const [mapping, setMapping] = useState<Mapping>({});
  const [requiredFieldsMapped, setRequiredFieldsMapped] = useState(false);
  const [nameFieldMapped, setNameFieldMapped] = useState(false);
  const [familyFieldMapped, setFamilyFieldMapped] = useState(false);
  const [attributeHeaderPattern, setAttributeHeaderPattern] = useState<string | null>(null);
  const [selectedFamily, setSelectedFamily] = useState<string | null>(null);
  const [familyAttributes, setFamilyAttributes] = useState<string[]>([]);
  const [loadingFamilyAttributes, setLoadingFamilyAttributes] = useState(false);
  const [invalidAttributeHeaders, setInvalidAttributeHeaders] = useState<string[]>([]);

  // Initialize translations
  const { getMappingText, getButtonText, getFieldTooltip, getMappingError, getGeneralError, t } =
    useImportTranslations();

  // Helper function to get translated field label
  const getFieldLabel = (field: ImportFieldSchemaEntry): string => {
    const translatedLabel = t(`imports.fields.${field.id}`, { defaultValue: '' });
    return translatedLabel || field.label;
  };

  // Fetch the field schema if not provided via props
  const {
    productFieldSchema: apiFieldSchema,
    attributeHeaderPattern: apiHeaderPattern,
    isLoading,
    isError,
  } = useImportFieldSchema();

  // Use prop schema if provided, otherwise fall back to API schema
  const fieldSchema = propFieldSchema || apiFieldSchema;

  // Check if we're in product import mode (for attribute-related logic)
  const isProductImport = importMode === 'products' || !importMode;

  // SKU validation for product imports
  const skuValidation = useSkuValidation({
    fullData,
    mapping,
    autoValidate: isProductImport, // Only validate for product imports
    debounceMs: 800, // Slightly longer debounce for user experience
    duplicateStrategy,
  });

  // Track attribute header pattern from API
  useEffect(() => {
    if (apiHeaderPattern) {
      setAttributeHeaderPattern(apiHeaderPattern);
    }
  }, [apiHeaderPattern]);

  // Process the schema to sort fields (required first, then alphabetically)
  const sortedFields = React.useMemo(() => {
    if (!fieldSchema) return [];

    return [...fieldSchema].sort((a, b) => {
      // Required fields first
      if (a.required && !b.required) return -1;
      if (!a.required && b.required) return 1;

      // Then alphabetically by label
      return a.label.localeCompare(b.label);
    });
  }, [fieldSchema]);

  // Check if header matches attribute pattern
  const isAttributeHeader = React.useCallback(
    (header: string): boolean => {
      if (!attributeHeaderPattern) return false;

      // First check if this header is mapped to a core field - if so, it's NOT an attribute
      const mappedField = mapping[header];
      if (mappedField && fieldSchema?.some(field => field.id === mappedField)) {
        return false;
      }

      // Check if the header matches any core field names - if so, it's NOT an attribute
      const coreFields = fieldSchema?.map(field => field.id.toLowerCase()) || [];
      const headerLower = header.toLowerCase().trim();

      // Exact matches or common variations
      const coreFieldVariations = [
        'sku',
        'name',
        'description',
        'brand',
        'category',
        'gtin',
        'barcode',
        'family',
        'family_code',
        'channel',
        'locale',
        'tags',
        'price',
      ];

      if (coreFieldVariations.includes(headerLower)) {
        return false;
      }

      // Also check against actual field schema
      if (coreFields.includes(headerLower)) {
        return false;
      }

      try {
        const regex = new RegExp(attributeHeaderPattern);
        const matchesPattern = regex.test(header);

        // Additional validation: attribute headers should typically contain at least one letter
        // and not be common system field names
        return (
          matchesPattern &&
          !/^(id|date|time|created|updated|status|active|enabled|disabled)(_.*)?$/i.test(header)
        );
      } catch (e) {
        captureValidationError(e as Error, {
          component: 'StepMapping',
          field: 'attributeHeaderPattern',
          value: attributeHeaderPattern,
        });
        stepMappingLogger.warn('Invalid regex pattern for attribute header detection', {
          pattern: attributeHeaderPattern,
          header,
          error: (e as Error).message,
        });
        return false;
      }
    },
    [attributeHeaderPattern, mapping, fieldSchema]
  );

  // Extract attribute name from header
  const getAttributeNameFromHeader = React.useCallback(
    (header: string): string => {
      if (!isAttributeHeader(header)) return '';

      // For natural language headers, extract the base attribute name by removing locale/channel suffixes
      // Format could be: "Bruto Weight", "Bruto Weight-en_US", "Bruto Weight-en_US-web"

      // Split by hyphens to separate potential locale/channel parts
      const parts = header.split('-');

      // First part is always the attribute name/label
      const attributeName = parts[0].trim();

      // Return the attribute name (could be code like "weight" or label like "Bruto Weight")
      return attributeName;
    },
    [isAttributeHeader]
  );

  // Check if a column has any non-empty values in the preview data
  const columnHasValues = React.useCallback(
    (header: string): boolean => {
      if (!previewData || previewData.length === 0) return false;

      return previewData.some(row => {
        const value = row[header];
        return value !== null && value !== undefined && String(value).trim() !== '';
      });
    },
    [previewData]
  );

  // Check if there are any attribute columns in the source headers
  const hasAttributeColumns = React.useMemo(() => {
    return sourceHeaders.some(header => isAttributeHeader(header));
  }, [sourceHeaders, isAttributeHeader]);

  // EFFECT 1: Fetch family attributes when family selection changes
  useEffect(() => {
    if (!selectedFamily) {
      setFamilyAttributes([]);
      return;
    }

    // Check cache first
    if (globalFamilyAttrCache[selectedFamily]) {
      setFamilyAttributes(globalFamilyAttrCache[selectedFamily]);
      return;
    }

    // Fetch from API
    setLoadingFamilyAttributes(true);
    getFamilyAttributes(selectedFamily)
      .then(response => {
        // Extract attribute codes from the API response
        const attributes = response.data.map((attr: any) => attr.code || attr.name);

        // Cache the result
        globalFamilyAttrCache[selectedFamily] = attributes;
        setFamilyAttributes(attributes);
      })
      .catch(error => {
        // Cache empty array to avoid infinite refetch loop on error or empty response
        globalFamilyAttrCache[selectedFamily] = [];
        setFamilyAttributes([]);
        logError(error instanceof Error ? error : new Error(String(error)), {
          component: 'StepMapping',
          action: 'getFamilyAttributes',
          metadata: { selectedFamily, error },
        });
        stepMappingLogger.error('Failed to fetch family attributes', {
          selectedFamily,
          error: error instanceof Error ? error.message : String(error),
        });
      })
      .finally(() => {
        setLoadingFamilyAttributes(false);
      });
  }, [selectedFamily]); // Only depend on selectedFamily, not the functions

  // EFFECT 2: Validate headers against family attributes when relevant data changes
  useEffect(() => {
    // Only validate if we have completed fetching (not loading) and have a selected family
    if (loadingFamilyAttributes || !selectedFamily) {
      setInvalidAttributeHeaders([]);
      return;
    }

    // Always validate, even if familyAttributes is empty (family with no attributes)
    const attributeHeaders = sourceHeaders.filter(header => isAttributeHeader(header));

    const invalid = attributeHeaders.filter(header => {
      const attrName = getAttributeNameFromHeader(header);
      const isNotInFamily = !familyAttributes.includes(attrName);
      const hasData = columnHasValues(header);

      // Only consider it invalid if the attribute is not in the family AND it has actual data
      return isNotInFamily && hasData;
    });

    setInvalidAttributeHeaders(invalid);
  }, [selectedFamily, familyAttributes, loadingFamilyAttributes]); // Only depend on primitive values

  // Auto-detect family value from preview data when family_code is mapped
  useEffect(() => {
    const familyMappedColumn = Object.keys(mapping).find(
      header => mapping[header] === 'family_code'
    );
    if (familyMappedColumn && previewData?.[0]?.[familyMappedColumn] && !selectedFamily) {
      const familyValue = previewData[0][familyMappedColumn];
      if (typeof familyValue === 'string' && familyValue.trim()) {
        setSelectedFamily(familyValue.trim());
      }
    }
  }, [mapping, previewData, selectedFamily]);

  // Auto-select fields where header matches API field (case-insensitive)
  useEffect(() => {
    if (!fieldSchema) return;

    // Try to load saved template first
    const savedMapping = localStorage.getItem(MAPPING_STORAGE_KEY);

    if (savedMapping) {
      try {
        const parsedMapping = JSON.parse(savedMapping) as Mapping;

        // Check if headers match saved mapping
        const savedHeaders = Object.keys(parsedMapping);
        const matchCount = savedHeaders.filter(h => sourceHeaders.includes(h)).length;
        const matchPercentage =
          savedHeaders.length > 0 ? (matchCount / savedHeaders.length) * 100 : 0;

        // If 70% or more of headers match, use saved mapping
        if (matchPercentage >= 70) {
          const filteredMapping: Mapping = {};

          // Only include mappings for headers that exist in current file
          for (const [source, target] of Object.entries(parsedMapping)) {
            if (sourceHeaders.includes(source)) {
              // Verify the target field still exists in the schema
              if (fieldSchema.some(field => field.id === target)) {
                filteredMapping[source] = target;
              }
            }
          }

          setMapping(filteredMapping);
          toast({
            title: getMappingText('templateLoaded.title'),
            description: getMappingText('templateLoaded.description'),
          });

          // Only log errors, removed success logging

          // Check required fields and name field
          checkMappingValidity(filteredMapping, fieldSchema);
          return;
        }
      } catch (e) {
        captureValidationError(e as Error, {
          component: 'StepMapping',
          field: 'savedMapping',
          value: savedMapping,
        });
        stepMappingLogger.error('Failed to parse saved mapping from local storage', {
          savedMappingLength: savedMapping.length,
          error: (e as Error).message,
        });
      }
    }

    // Auto-map fields with similar names
    const autoMapping: Mapping = {};

    sourceHeaders.forEach(header => {
      const normalizedHeader = header.toLowerCase().trim();

      // Try to find a matching field by ID or label
      const matchedField = fieldSchema.find(field => {
        // Handle special case for GTIN/Barcode
        if (
          field.id === 'gtin' &&
          (normalizedHeader.includes('barcode') ||
            normalizedHeader.includes('upc') ||
            normalizedHeader.includes('ean'))
        ) {
          return true;
        }

        // Handle special case for family/family_code
        if (field.id === 'family_code' && normalizedHeader.includes('family')) {
          return true;
        }

        return (
          field.id.toLowerCase() === normalizedHeader ||
          field.label.toLowerCase() === normalizedHeader
        );
      });

      if (matchedField) {
        autoMapping[header] = matchedField.id;
      }
    });

    setMapping(autoMapping);
    checkMappingValidity(autoMapping, fieldSchema);
  }, [sourceHeaders, fieldSchema, getMappingText]);

  // Check if required fields are mapped and if name and family are mapped
  const checkMappingValidity = (currentMapping: Mapping, schema: ImportFieldSchemaEntry[]) => {
    if (!schema) return { requiredMapped: false, nameMapped: false, familyMapped: false };

    const mappedFields = Object.values(currentMapping);

    // Check if all required fields are mapped
    const requiredFields = schema.filter(f => f.required).map(f => f.id);
    const allRequiredMapped = requiredFields.every(field => mappedFields.includes(field));
    setRequiredFieldsMapped(allRequiredMapped);

    // Check if name field is mapped (optional but recommended)
    const isNameMapped = mappedFields.includes('name');
    setNameFieldMapped(isNameMapped);

    // Check if family field is mapped (needed for attribute values)
    const isFamilyMapped = mappedFields.includes('family_code');
    setFamilyFieldMapped(isFamilyMapped);

    return {
      requiredMapped: allRequiredMapped,
      nameMapped: isNameMapped,
      familyMapped: isFamilyMapped,
    };
  };

  // Handle field mapping change
  const handleMappingChange = (source: string, target: string) => {
    // If target is 'skip', it means "do not import" this field
    const newMapping = { ...mapping };

    if (target === 'skip') {
      // Remove the mapping if it exists
      const previousTarget = mapping[source];
      delete newMapping[source];

      // If we just unmapped the family_code, clear the selected family
      if (previousTarget === 'family_code') {
        setSelectedFamily(null);
      }
    } else {
      // Add or update the mapping
      newMapping[source] = target;

      // If this is a family_code mapping, fetch the family attributes
      if (target === 'family_code') {
        const familyValue = previewData?.[0]?.[source];
        if (familyValue && typeof familyValue === 'string' && familyValue.trim()) {
          setSelectedFamily(familyValue.trim());
        }
      }
    }

    setMapping(newMapping);
    if (fieldSchema) {
      checkMappingValidity(newMapping, fieldSchema);
    }
  };

  const handleComplete = () => {
    // First save the template to localStorage for auto-mapping future imports
    localStorage.setItem(MAPPING_STORAGE_KEY, JSON.stringify(mapping));

    // Then call the callback
    onMappingComplete(mapping);
  };

  // If still loading the schema, show skeleton loaders
  if (isLoading && !propFieldSchema) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 w-full" data-testid="skeleton" />
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-40" data-testid="skeleton" />
          <Skeleton className="h-8 w-32" data-testid="skeleton" />
        </div>
        <Skeleton className="h-64 w-full" data-testid="skeleton" />
      </div>
    );
  }

  // If there was an error loading the schema
  if ((isError || !fieldSchema) && !propFieldSchema) {
    const errorInfo = getGeneralError('unexpectedError');
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-6">
        <h3 className="mb-2 text-lg font-medium text-red-800">
          <AlertCircleIcon className="mr-2 inline size-5" />
          {errorInfo.title}
        </h3>
        <p className="text-red-700">{errorInfo.description}</p>
        <Button className="mt-4" variant="outline" onClick={() => window.location.reload()}>
          {getButtonText('retry')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="mb-4 rounded-md bg-muted p-4">
        <h3 className="mb-2 font-medium">{getMappingText('title')}</h3>
        <p className="mb-2 text-sm text-muted-foreground">{getMappingText('description')}</p>
        {isProductImport && (
          <div className="mb-3 text-xs text-muted-foreground">
            <strong>{getMappingText('important')}:</strong> {getMappingText('attributeDetection')}
            <br />
            <strong>{getMappingText('important')}:</strong> {getMappingText('validation')}
          </div>
        )}
        {getMappingText('recommendation') && (
          <div className="mb-3 text-xs text-muted-foreground">
            <strong>{getMappingText('important')}:</strong> {getMappingText('recommendation')}
          </div>
        )}
        <div className="mb-3 flex flex-wrap gap-2">
          {fieldSchema
            ?.filter(f => f.required)
            .map(field => (
              <Badge
                key={field.id}
                variant="outline"
                className="border-red-200 bg-red-50 text-red-700"
              >
                {getFieldLabel(field)} *
              </Badge>
            ))}
        </div>

        <div className="flex flex-wrap gap-2">
          <h4 className="w-full text-sm font-medium">{getMappingText('availableFields')}</h4>
          {sortedFields.map(field => (
            <TooltipProvider key={field.id}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className={`${
                      Object.values(mapping).includes(field.id)
                        ? 'border-green-200 bg-green-50 text-green-700'
                        : field.id === 'family_code'
                          ? 'border-amber-200 bg-amber-50 text-amber-700'
                          : 'bg-gray-50'
                    } cursor-help`}
                  >
                    {getFieldLabel(field)}
                    {field.required ? ' *' : ''}
                    {field.id === 'family_code' && isProductImport && (
                      <span className="ml-1">👪</span>
                    )}
                    {getFieldTooltip(field.id) && <InfoIcon className="ml-1 inline size-3" />}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="w-[200px] text-xs">
                    {getFieldTooltip(field.id) || `Field type: ${field.type}`}
                    {field.id === 'family_code' &&
                      !familyFieldMapped &&
                      hasAttributeColumns &&
                      isProductImport && (
                        <span className="mt-1 block font-medium text-amber-600">
                          {getMappingText('requiresFamily')}!
                        </span>
                      )}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
        </div>
      </div>

      {/* Show warnings */}
      <div className="space-y-3">
        {/* Name field warning - only for product imports */}
        {isProductImport && !nameFieldMapped && (
          <Alert className="border-amber-200 bg-amber-50">
            <AlertCircleIcon className="size-4 text-amber-700" />
            <AlertDescription className="text-amber-700">
              {getMappingText('nameFieldRecommendedDesc')}
            </AlertDescription>
          </Alert>
        )}

        {/* Family field warning if attribute columns are present - only for product imports */}
        {isProductImport && !familyFieldMapped && hasAttributeColumns && (
          <Alert className="border-amber-200 bg-amber-50">
            <AlertCircleIcon className="size-4 text-amber-700" />
            <AlertTitle className="font-medium text-amber-700">
              {getMappingError('familyRequired').title}
            </AlertTitle>
            <AlertDescription className="text-amber-700">
              {getMappingError('familyRequired').description}
            </AlertDescription>
          </Alert>
        )}

        {/* Invalid attribute headers warning - only for product imports */}
        {isProductImport &&
          familyFieldMapped &&
          selectedFamily &&
          invalidAttributeHeaders.length > 0 && (
            <Alert className="border-amber-200 bg-amber-50">
              <AlertCircleIcon className="size-4 text-amber-700" />
              <AlertTitle className="font-medium text-amber-700">
                {getMappingText('unknownAttributes')}
              </AlertTitle>
              <AlertDescription className="text-amber-700">
                {getMappingText('unknownAttributesDesc', {
                  familyCode: selectedFamily,
                  attributes: invalidAttributeHeaders.join(', '),
                })}
              </AlertDescription>
            </Alert>
          )}

        {/* SKU validation warning - only for product imports */}
        {isProductImport && skuValidation.hasSkuColumn && (
          <SkuValidationWarning
            validationResult={skuValidation}
            onRefresh={skuValidation.validate}
            showDetails={true}
          />
        )}

        {/* Loading family attributes indicator - only for product imports */}
        {isProductImport && loadingFamilyAttributes && (
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <div className="size-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
            <span>{getMappingText('checkingFamilyAttributes')}</span>
          </div>
        )}
      </div>

      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-medium">{getMappingText('mapYourColumns')}</h3>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{getMappingText('sourceColumn')}</TableHead>
              <TableHead>{getMappingText('mapToField')}</TableHead>
              <TableHead>{getMappingText('preview')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[...new Set(sourceHeaders)].map(header => {
              const isAttribute = isProductImport && isAttributeHeader(header);
              const isInvalidAttribute = isAttribute && invalidAttributeHeaders.includes(header);
              return (
                <TableRow key={header}>
                  <TableCell className="font-medium">
                    {header}
                    {isAttribute && (
                      <Badge
                        className={`ml-2 ${isInvalidAttribute ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'} hover:bg-gray-200`}
                      >
                        <CheckCircleIcon
                          className={`mr-1 size-3 ${isInvalidAttribute ? 'text-amber-600' : 'text-green-600'}`}
                        />
                        {getMappingText('attribute')} {isInvalidAttribute ? '⚠️' : '✓'}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Select
                      value={mapping[header] || 'skip'}
                      onValueChange={value => handleMappingChange(header, value)}
                    >
                      <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder={t('imports.placeholders.selectField')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="skip">{getMappingText('skip')}</SelectItem>
                        {sortedFields.map(field => (
                          <SelectItem key={field.id} value={field.id}>
                            {getFieldLabel(field)}
                            {field.required ? ' *' : ''}
                            {field.id === 'family_code' && isProductImport && (
                              <span className="ml-1">👪</span>
                            )}
                            {getFieldTooltip(field.id) && (
                              <InfoIcon className="ml-1 inline size-3 text-muted-foreground" />
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {/* Always show actual data value from the first row if available */}
                    {previewData &&
                      previewData.length > 0 &&
                      previewData[0][header] !== undefined && (
                        <div className="mb-2">
                          <span className="text-xs text-gray-500">
                            {getMappingText('sampleValue')}
                          </span>
                          <br />
                          <span className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">
                            {String(previewData[0][header]).substring(0, 50)}
                            {String(previewData[0][header]).length > 50 ? '...' : ''}
                          </span>
                        </div>
                      )}
                    {mapping[header] && fieldSchema && (
                      <div>
                        <span className="text-xs text-gray-500">{getMappingText('mappedTo')}</span>
                        <br />
                        <code className="rounded bg-muted px-2 py-1">{mapping[header]}</code>
                        {getFieldTooltip(mapping[header]) && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            {getFieldTooltip(mapping[header])}
                          </div>
                        )}
                      </div>
                    )}
                    {isAttribute && !mapping[header] && (
                      <div className="text-xs text-green-600">
                        <p>{getMappingText('willImportAsAttribute')}</p>
                        <p className="mt-1 text-muted-foreground">
                          {getMappingText('systemWillMatch')}
                        </p>
                        {!familyFieldMapped && (
                          <p className="mt-1 text-amber-600">{getMappingText('requiresFamily')}</p>
                        )}
                        {isInvalidAttribute && familyFieldMapped && (
                          <p className="mt-1 text-amber-600">
                            {getMappingText('notFoundInFamilyWithData', {
                              familyCode: selectedFamily,
                            })}
                          </p>
                        )}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="flex justify-end">
        <Button onClick={handleComplete} disabled={!requiredFieldsMapped}>
          {getButtonText('next')}
        </Button>
      </div>
    </div>
  );
};

export default StepMapping;
